import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { PayMethods } from "@/components/PayMethods";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UNLOCK_FEE, APPLICATION_FEE, CONTACT_PHONE, CONTACT_EMAIL } from "@/lib/aps";
import {
  Lock, GraduationCap, MapPin, Clock, Loader2, ArrowRight, Sparkles, ShieldCheck, Compass, RefreshCw,
} from "lucide-react";

function money(n) {
  return `R${Number(n).toFixed(2)}`;
}

export function CoursesInner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);
  const [selected, setSelected] = useState({}); // { uniCode: [courseId, ...] }
  const [fields, setFields] = useState([]);
  const [field, setField] = useState("All");

  const load = (fld) => {
    const params = fld && fld !== "All" ? { field: fld } : {};
    return api.get("/courses/qualifying", { params }).then(({ data }) => setData(data));
  };
  useEffect(() => {
    api.get("/fields").then(({ data }) => setFields(data.fields));
    // Self-heal payments confirmed at Paystack but missed on redirect (e.g. Apple Pay / Capitec Pay), then load.
    load("All");
    api.post("/payments/reconcile").then(({ data: r }) => { if (r.reconciled > 0) load("All"); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recheck = async () => {
    try {
      const { data } = await api.post("/payments/reconcile");
      await load(field);
      toast[data.reconciled > 0 ? "success" : "info"](
        data.reconciled > 0 ? "Payment confirmed — courses unlocked!" : "No confirmed payment found yet. If you've paid, please wait a minute and try again, or contact us."
      );
    } catch { toast.error("Could not re-check. Please try again."); }
  };

  const onFieldChange = (val) => {
    setField(val);
    setSelected({});
    load(val);
  };

  const unlock = async () => {
    setPaying(true);
    try {
      const callback_url = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/payments/initialize", { type: "unlock", callback_url });
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPaying(false);
    }
  };

  const toggleCourse = (uni, courseId) => {
    setSelected((prev) => {
      const cur = prev[uni] || [];
      if (cur.includes(courseId)) return { ...prev, [uni]: cur.filter((c) => c !== courseId) };
      if (cur.length >= 3) {
        toast.error("You can choose a maximum of 3 courses per university.");
        return prev;
      }
      return { ...prev, [uni]: [...cur, courseId] };
    });
  };

  const selEntries = Object.entries(selected).filter(([, ids]) => ids.length > 0);
  const totalUnis = selEntries.length;
  const totalCourses = selEntries.reduce((s, [, ids]) => s + ids.length, 0);
  const appFee = data?.courses?.[0]?.application_fee ?? APPLICATION_FEE;
  const totalFee = totalUnis * appFee;

  const checkout = () => {
    if (!totalUnis) {
      toast.error("Select at least 1 course to apply.");
      return;
    }
    const selections = selEntries.map(([university, course_ids]) => ({ university, course_ids }));
    sessionStorage.setItem("yva_cart", JSON.stringify(selections));
    navigate("/dashboard/apply-batch");
  };

  if (!data)
    return (
      <div>
        <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#0B2447]" /></div>
      </div>
    );

  if (data.count === 0 && field === "All")
    return (
      <div>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <GraduationCap className="w-14 h-14 text-slate-300 mx-auto" />
          <h1 className="font-display text-3xl font-bold text-[#0B2447] mt-6">No matches yet</h1>
          <p className="text-slate-600 mt-3">
            We couldn't find courses matching your current marks, or you haven't entered them yet.
          </p>
          <Button className="mt-6 bg-[#0B2447] hover:bg-[#153461]" onClick={() => navigate("/dashboard/marks")}
            data-testid="go-to-marks-btn">Enter Your Marks</Button>
        </div>
      </div>
    );

  // group unlocked courses by university
  const groups = {};
  (data.courses || []).forEach((c) => {
    if (!groups[c.university]) groups[c.university] = { name: c.university_name, logo: c.university_logo, fee: c.application_fee, courses: [] };
    groups[c.university].courses.push(c);
  });

  return (
    <div>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Step 2</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0B2447] mt-1">
          You qualify for {data.count} course{data.count !== 1 ? "s" : ""}
        </h1>
        <p className="text-slate-600 mt-2">
          APS <span className="font-bold text-[#0B2447]">{data.aps}</span>. Choose your field of study, then pick up to
          <span className="font-semibold text-[#0B2447]"> 3 courses per university</span>.
          Each application is a flat <span className="font-semibold text-[#0B2447]">R{appFee.toFixed(2)}</span> service fee.
        </p>

        <div className="mt-5 bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap items-center gap-3"
          data-testid="field-selector-bar">
          <span className="flex items-center gap-2 text-sm font-semibold text-[#0B2447]">
            <Compass className="w-4 h-4 text-[#F5C32C]" /> Field of study
          </span>
          <Select value={field} onValueChange={onFieldChange}>
            <SelectTrigger className="w-full sm:w-72 border-slate-300" data-testid="field-select">
              <SelectValue placeholder="Choose a field" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All fields</SelectItem>
              {fields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-400">Showing courses across all universities you qualify for.</span>
        </div>

        {data.count === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="no-field-matches">
            You don't qualify for any courses in <span className="font-semibold text-[#0B2447]">{field}</span> with your current APS. Try another field.
          </div>
        ) : !data.unlocked ? (
          <LockedView teaser={data.teaser} onUnlock={unlock} paying={paying} count={data.count} unlockFee={data.unlock_fee} onRecheck={recheck} />
        ) : (
          <div className="mt-8 space-y-6" data-testid="courses-grid">
            {Object.entries(groups).map(([uni, g]) => {
              const sel = selected[uni] || [];
              return (
                <div key={uni} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
                  data-testid={`uni-group-${uni}`}>
                  <div className="flex items-center justify-between gap-4 p-5 bg-[#0B2447]/[0.03] border-b border-slate-200">
                    <div className="flex items-center gap-4">
                      <img src={g.logo} alt={g.name} className="w-12 h-12 rounded-md object-contain bg-white border border-slate-100" />
                      <div>
                        <h2 className="font-display font-bold text-lg text-[#0B2447]">{g.name}</h2>
                        <p className="text-xs text-slate-500">{g.courses.length} matching course{g.courses.length !== 1 ? "s" : ""}</p>
                      </div>
                    </div>
                    <Badge className="border-0 bg-[#F5C32C]/20 text-[#8a6d0b]">
                      Application {money(g.fee)}
                    </Badge>
                  </div>

                  <div className="divide-y divide-slate-100">
                    {g.courses.map((c) => {
                      const checked = sel.includes(c.id);
                      return (
                        <label key={c.id}
                          className={`flex items-start gap-3 p-4 cursor-pointer transition-colors ${checked ? "bg-[#F5C32C]/[0.06]" : "hover:bg-slate-50"}`}
                          data-testid={`course-row-${c.id}`}>
                          <Checkbox checked={checked} onCheckedChange={() => toggleCourse(uni, c.id)}
                            disabled={!checked && sel.length >= 3}
                            data-testid={`course-check-${c.id}`} className="mt-1" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-[#0B2447]">{c.name}</span>
                              <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-[10px]">{c.faculty}</Badge>
                            </div>
                            <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {c.campus}</span>
                              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {c.duration_years} yr</span>
                              <span>APS {c.aps_required}</span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between gap-4 p-4 bg-slate-50 border-t border-slate-200">
                    <span className="text-sm text-slate-500">
                      {sel.length}/3 selected at this university
                    </span>
                    {sel.length > 0 && (
                      <span className="text-sm font-semibold text-[#0B2447]" data-testid={`uni-selected-${uni}`}>
                        Added to your applications
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {data.unlocked && totalUnis > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-2xl"
          data-testid="cart-bar">
          <div className="max-w-5xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-display text-lg font-bold text-[#0B2447]">
                {totalUnis} universit{totalUnis !== 1 ? "ies" : "y"} · {totalCourses} course{totalCourses !== 1 ? "s" : ""}
              </div>
              <p className="text-xs text-slate-500">One combined payment · R{appFee.toFixed(2)} per university</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="font-display text-2xl font-bold text-[#0B2447]" data-testid="cart-total">R{totalFee.toFixed(2)}</div>
              <Button onClick={checkout} data-testid="cart-checkout-btn"
                className="bg-[#F5C32C] hover:bg-[#E3B220] text-[#0B2447] font-bold py-6 px-8">
                Continue to Application <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Courses() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      <CoursesInner />
    </div>
  );
}

function LockedView({ teaser, onUnlock, paying, count, unlockFee, onRecheck }) {
  const fee = unlockFee ?? UNLOCK_FEE;
  const universities = [...new Set((teaser || []).map((t) => t.university_name))];
  return (
    <div className="relative mt-8">
      <div className="grid md:grid-cols-2 gap-5 blur-md pointer-events-none select-none" aria-hidden>
        {Array.from({ length: Math.min(count, 6) }).map((_, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="h-5 w-24 bg-slate-200 rounded mb-3" />
            <div className="h-6 w-3/4 bg-slate-300 rounded mb-2" />
            <div className="h-4 w-1/2 bg-slate-200 rounded" />
            <div className="h-10 w-full bg-slate-200 rounded mt-5" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex items-start justify-center pt-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-xl border border-slate-200 rounded-2xl p-8 shadow-2xl max-w-md w-full mx-4"
          data-testid="unlock-card">
          <div className="w-14 h-14 rounded-xl bg-[#0B2447] flex items-center justify-center mx-auto">
            <Lock className="w-7 h-7 text-[#F5C32C]" />
          </div>
          <h2 className="font-display text-2xl font-bold text-[#0B2447] text-center mt-5">Unlock Your Matches</h2>
          <p className="text-slate-600 text-center mt-2 text-sm">
            You qualify for <span className="font-bold text-[#0B2447]">{count}</span> course
            {count !== 1 ? "s" : ""} across {universities.length} universit{universities.length !== 1 ? "ies" : "y"}.
          </p>
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            {universities.map((u) => <Badge key={u} variant="secondary" className="bg-[#0B2447]/5 text-[#0B2447]">{u}</Badge>)}
          </div>
          <div className="text-center my-6">
            <span className="font-display text-5xl font-bold text-[#0B2447]">R{fee.toFixed(2)}</span>
            <p className="text-xs text-slate-400 mt-1">Once-off · Non-refundable</p>
          </div>
          <PayMethods className="mb-4" />
          <Button onClick={onUnlock} disabled={paying} data-testid="unlock-pay-btn"
            className="w-full bg-[#F5C32C] hover:bg-[#E3B220] text-[#0B2447] font-bold py-6 text-base">
            {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Unlock for R{fee.toFixed(2)}</>}
          </Button>
          <p className="flex items-center justify-center gap-1.5 mt-4 text-xs text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5" /> Secured by Paystack · Card, Apple Pay & Capitec Pay
          </p>
          <div className="mt-5 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="already-paid-note">
            <p className="font-semibold">Already paid but still locked?</p>
            <p className="mt-1">Apple Pay / Capitec Pay can take a minute to confirm. <strong>Please do NOT pay the R{fee.toFixed(2)} twice.</strong> Tap re-check below, or contact us on WhatsApp {CONTACT_PHONE} / {CONTACT_EMAIL}.</p>
            <Button onClick={onRecheck} variant="outline" size="sm" data-testid="recheck-unlock-btn"
              className="mt-2 border-amber-400 text-amber-800 hover:bg-amber-100">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> I've paid — re-check
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
